/**
 * VorXOC – MITRE ATT&CK Sample Data
 * Used across Reports tabs: Dashboard, MITRE Assessment, Security Posture
 */

export const REPORT_PERIOD = 'May 1 – May 25, 2026';
export const REPORT_ORG    = 'Acme Corp';

// ─── MITRE Tactics ────────────────────────────────────────────────────────────

export const MITRE_TACTICS = [
  { id: 'TA0001', name: 'Initial Access',        detections: 23, techniques: 5, covered: true  },
  { id: 'TA0002', name: 'Execution',              detections: 41, techniques: 8, covered: true  },
  { id: 'TA0003', name: 'Persistence',            detections: 18, techniques: 4, covered: true  },
  { id: 'TA0004', name: 'Privilege Escalation',   detections: 31, techniques: 6, covered: true  },
  { id: 'TA0005', name: 'Defense Evasion',        detections: 15, techniques: 7, covered: true  },
  { id: 'TA0006', name: 'Credential Access',      detections: 37, techniques: 5, covered: true  },
  { id: 'TA0007', name: 'Discovery',              detections: 52, techniques: 9, covered: true  },
  { id: 'TA0008', name: 'Lateral Movement',       detections: 12, techniques: 3, covered: true  },
  { id: 'TA0009', name: 'Collection',             detections:  8, techniques: 2, covered: false },
  { id: 'TA0010', name: 'Exfiltration',           detections:  6, techniques: 1, covered: false },
  { id: 'TA0011', name: 'Command & Control',      detections: 29, techniques: 6, covered: true  },
  { id: 'TA0040', name: 'Impact',                 detections:  4, techniques: 1, covered: false },
];

// ─── Top Detected Techniques ──────────────────────────────────────────────────

export const TOP_TECHNIQUES = [
  { id: 'T1082', name: 'System Information Discovery',       tactic: 'Discovery',          detections: 44, severity: 'Low',      status: 'Resolved'    },
  { id: 'T1059', name: 'Command and Scripting Interpreter',  tactic: 'Execution',          detections: 28, severity: 'Critical', status: 'Investigating' },
  { id: 'T1110', name: 'Brute Force',                        tactic: 'Credential Access',  detections: 31, severity: 'High',     status: 'Resolved'    },
  { id: 'T1078', name: 'Valid Accounts',                     tactic: 'Initial Access',     detections: 19, severity: 'High',     status: 'Investigating' },
  { id: 'T1566', name: 'Phishing',                           tactic: 'Initial Access',     detections: 18, severity: 'High',     status: 'Resolved'    },
  { id: 'T1055', name: 'Process Injection',                  tactic: 'Defense Evasion',    detections: 15, severity: 'High',     status: 'Resolved'    },
  { id: 'T1021', name: 'Remote Services',                    tactic: 'Lateral Movement',   detections: 11, severity: 'High',     status: 'Resolved'    },
  { id: 'T1003', name: 'OS Credential Dumping',              tactic: 'Credential Access',  detections:  9, severity: 'Critical', status: 'Resolved'    },
  { id: 'T1071', name: 'Application Layer Protocol',         tactic: 'Command & Control',  detections:  8, severity: 'Medium',   status: 'Monitoring'  },
  { id: 'T1486', name: 'Data Encrypted for Impact',          tactic: 'Impact',             detections:  3, severity: 'Critical', status: 'Resolved'    },
];

// ─── Security Posture ─────────────────────────────────────────────────────────

export const POSTURE_DOMAINS = [
  { name: 'Detection',   score: 78, benchmark: 70, description: 'Ability to detect known attack patterns and anomalies' },
  { name: 'Prevention',  score: 82, benchmark: 75, description: 'Controls that prevent threats from executing'           },
  { name: 'Response',    score: 71, benchmark: 65, description: 'Speed and quality of incident response'                 },
  { name: 'Coverage',    score: 67, benchmark: 70, description: 'Breadth of MITRE ATT&CK tactic coverage'               },
  { name: 'Resilience',  score: 74, benchmark: 70, description: 'Ability to recover and maintain operations'             },
];

export const OVERALL_POSTURE_SCORE = 74;

// ─── Control Gaps (for Security Posture report) ───────────────────────────────

export const CONTROL_GAPS = [
  { tactic: 'Collection',    gap: 'No DLP monitoring in place',                   priority: 'High',   effort: 'Medium' },
  { tactic: 'Exfiltration',  gap: 'Egress filtering not enforced on all segments', priority: 'High',   effort: 'Medium' },
  { tactic: 'Impact',        gap: 'Backup integrity checks not automated',         priority: 'Medium', effort: 'Low'    },
  { tactic: 'Discovery',     gap: 'Excessive read permissions on file shares',     priority: 'Medium', effort: 'Low'    },
];

// ─── Incident Trend (monthly, last 6 months) ──────────────────────────────────

export const INCIDENT_TREND = [
  { month: 'Dec', critical: 12, high: 23, medium: 45, low: 18 },
  { month: 'Jan', critical:  8, high: 19, medium: 52, low: 22 },
  { month: 'Feb', critical: 15, high: 28, medium: 38, low: 25 },
  { month: 'Mar', critical:  6, high: 21, medium: 43, low: 19 },
  { month: 'Apr', critical: 11, high: 25, medium: 49, low: 21 },
  { month: 'May', critical:  9, high: 18, medium: 41, low: 17 },
];

// ─── Key Metrics ──────────────────────────────────────────────────────────────

export const KEY_METRICS = {
  totalIncidents:     280,
  criticalOpen:         9,
  mitreTacticsHit:      9,   // out of 12
  techniquesCovered:   55,
  avgDetectTime:      '2.3h',
  avgResolveTime:     '6.1h',
  threatsBlocked:    1247,
  postureScore:        74,
};

// ─── Executive Security Posture Data ─────────────────────────────────────────

export const EXEC_POSTURE_PILLARS = [
  {
    id:        'identity',
    icon:      '🔑',
    name:      'Identity & Access Control',
    question:  'Are the right people able to access the right things — and nothing more?',
    score:     82,
    benchmark: 75,
    grade:     'Strong',
    gradeColor:'#059669',
    whatItMeans: 'This measures how well we control who can access your systems, data, and applications. A strong score means your digital "keys" are well managed — staff access only what they need, and lost or stolen passwords are quickly caught.',
    whatWeDo: 'We enforce Multi-Factor Authentication (MFA), monitor every login attempt, and use Azure Active Directory Conditional Access policies to block suspicious logins automatically. Administrator accounts have time-limited privileges.',
    wins:      ['MFA coverage at 77% of accounts (up from 61% last quarter)', '142 account takeover attempts blocked', 'All admin accounts reviewed and cleaned up'],
    gap:       '23% of accounts still do not have MFA enforced. This is the single fastest win available.',
    gapColor:  '#ca8a04',
  },
  {
    id:        'detection',
    icon:      '👁',
    name:      'Threat Monitoring & Detection',
    question:  'Are we watching for threats 24 hours a day, 7 days a week?',
    score:     78,
    benchmark: 70,
    grade:     'Good',
    gradeColor:'#059669',
    whatItMeans: 'This measures how quickly and reliably we spot an attack in progress. The faster we detect a threat, the less damage it can do. Our current average detection time of 2.3 hours is better than the 4-hour industry average.',
    whatWeDo:  'Azure Sentinel collects signals from every device, application, and user account — processing over 2 million log events daily. Our SOC analysts are alerted immediately when suspicious patterns emerge, day or night.',
    wins:      ['Average detection time: 2.3 hours (industry avg: 4 hours)', '1,247 threats detected and stopped this period', '9 of 12 attack categories covered by automated alerts'],
    gap:       '3 attack categories (data collection, exfiltration, destructive attacks) lack automated detection. These represent blind spots that require attention.',
    gapColor:  '#dc2626',
  },
  {
    id:        'response',
    icon:      '⚡',
    name:      'Incident Response & Containment',
    question:  'When something goes wrong, how fast and effectively do we respond?',
    score:     71,
    benchmark: 65,
    grade:     'Good',
    gradeColor:'#059669',
    whatItMeans: 'Speed matters in cyber security — every minute an attacker is active increases the potential damage. This score measures how quickly our team contains and resolves incidents once detected.',
    whatWeDo:  'Our SOC team operates with documented response playbooks for every incident type. High-severity incidents trigger immediate escalation with a target containment time of under 1 hour. Affected systems are isolated automatically while the team investigates.',
    wins:      ['97% of incidents resolved this period', 'Average resolution time: 6.1 hours', 'Ransomware attempt contained and recovered within 45 minutes'],
    gap:       'Response playbooks cover 7 of 11 incident types. 4 scenario types (advanced persistent threats, supply-chain attacks, insider threats, cloud misconfigurations) lack formal response procedures.',
    gapColor:  '#ca8a04',
  },
  {
    id:        'coverage',
    icon:      '🗺',
    name:      'Security Coverage & Breadth',
    question:  'Are there areas of our environment that have no security monitoring?',
    score:     67,
    benchmark: 70,
    grade:     'Needs Attention',
    gradeColor:'#ca8a04',
    whatItMeans: 'This measures whether every part of your technology environment — devices, cloud services, applications — is being monitored. Gaps in coverage are opportunities for attackers to go undetected.',
    whatWeDo:  'Microsoft Defender covers all managed Windows and Mac devices. Azure Sentinel monitors cloud workloads and Microsoft 365. Network monitoring is in place for headquarters and primary office locations.',
    wins:      ['All 230 managed endpoints enrolled in Defender', 'Microsoft 365 and Azure cloud fully monitored', 'Network traffic analysis covers 85% of office locations'],
    gap:       '3 remote office locations are not yet connected to central monitoring. Mobile devices (BYOD) are not fully enrolled in Defender. These represent real blind spots.',
    gapColor:  '#dc2626',
  },
  {
    id:        'resilience',
    icon:      '🔄',
    name:      'Business Continuity & Recovery',
    question:  'If a cyber attack succeeds, can the business recover quickly?',
    score:     74,
    benchmark: 70,
    grade:     'Good',
    gradeColor:'#059669',
    whatItMeans: 'Even the best defences can be breached. This score measures how prepared the organisation is to survive and recover from a successful attack — particularly ransomware, which could otherwise shut down operations.',
    whatWeDo:  'Automated backups run daily for all critical systems. Backup data is stored in a separate, isolated environment that attackers cannot reach from the main network. Our disaster recovery plan covers critical business systems.',
    wins:      ['Automated backups covering 92% of critical systems', 'Backup isolation policy enforced — backups cannot be deleted by ransomware', 'Recovery time for most systems estimated under 4 hours'],
    gap:       'Backups have not been formally tested via a recovery drill in 18 months. Without testing, we cannot confirm recovery time estimates are accurate.',
    gapColor:  '#ca8a04',
  },
];

export const EXEC_POSTURE_ROI = {
  incidentsBlocked:  1247,
  estimatedCostPerIncident: 8400,
  estimatedValueProtected:  '~$10.5M',
  platformCostEstimate: '$240K/year',
  roi: '43:1',
  statement: "Based on industry-average breach costs, the 1,247 threats blocked this period represent an estimated $10.5M in avoided incident costs — ransomware recovery, legal fees, regulatory fines, and operational downtime. Our security platform investment of approximately $240K/year delivers an estimated 43:1 return on investment.",
};

export const EXEC_POSTURE_BENCHMARK = [
  { domain: 'Identity Control',   our: 82, bench: 75 },
  { domain: 'Threat Detection',   our: 78, bench: 70 },
  { domain: 'Incident Response',  our: 71, bench: 65 },
  { domain: 'Security Coverage',  our: 67, bench: 70 },
  { domain: 'Business Continuity',our: 74, bench: 70 },
];

export const EXEC_POSTURE_ROADMAP = [
  {
    priority: 'Urgent',
    color:    '#dc2626',
    timeline: '2 weeks',
    action:   'Enforce MFA on remaining 23% of accounts',
    businessCase: 'MFA blocks 99.9% of automated password attacks. This is the highest-value security action available with zero additional cost.',
    owner:    'IT & HR to coordinate roll-out',
    cost:     'No cost — already included in Microsoft 365 licence',
  },
  {
    priority: 'High',
    color:    '#ea580c',
    timeline: '4 weeks',
    action:   'Deploy Data Loss Prevention monitoring',
    businessCase: 'We currently cannot reliably detect sensitive data being copied outside the organisation. One successful data theft incident could trigger GDPR fines up to 4% of global revenue.',
    owner:    'IT Security team (configuration only)',
    cost:     'No cost — included in Microsoft 365 E5 licence',
  },
  {
    priority: 'High',
    color:    '#ea580c',
    timeline: '6 weeks',
    action:   'Connect 3 remote offices to central monitoring',
    businessCase: 'These offices are currently blind spots. An attacker accessing the network through a remote office could go undetected for weeks.',
    owner:    'IT Infrastructure team',
    cost:     'Estimated $12K one-time setup cost',
  },
  {
    priority: 'Medium',
    color:    '#ca8a04',
    timeline: '8 weeks',
    action:   'Run ransomware recovery drill',
    businessCase: 'Backups exist but have never been fully tested. A 4-hour recovery drill confirms the business can survive a worst-case ransomware attack and gives the board confidence in continuity plans.',
    owner:    'IT & Business Continuity team',
    cost:     'Half-day business exercise — no technology cost',
  },
];

// ─── Executive Threat Categories (MITRE → Business Language) ─────────────────

export const EXEC_THREAT_CATEGORIES = [
  {
    id:          'identity',
    icon:        '🔑',
    name:        'Identity & Account Attacks',
    businessDesc:'Criminals attempting to steal employee passwords, bypass login controls, or take over accounts — often the first step in a larger attack.',
    mitreRef:    'Initial Access · Credential Access',
    detections:  60,
    trend:       'up',
    trendPct:    12,
    status:      'protected',
    howWeDetect: 'Azure Sentinel monitors every sign-in attempt across all Microsoft 365 accounts. Any unusual pattern — such as logins from new countries, hundreds of failed attempts, or access outside business hours — triggers an immediate alert.',
    howWeDefend: 'Suspicious accounts are automatically blocked via Conditional Access policies. Our SOC team reviews each alert, resets compromised credentials, and adds attacker IP addresses to blocklists within minutes.',
    topThreats:  ['Password spray across 142 accounts (blocked)', 'Phishing emails to HR team (quarantined)', 'Stolen credential use from external IP (blocked)'],
    businessRisk:'Compromised accounts give attackers the same access as your employees — including emails, files, and financial systems.',
  },
  {
    id:          'malware',
    icon:        '🦠',
    name:        'Malware & Ransomware',
    businessDesc:'Malicious software designed to damage systems, encrypt files for ransom, or silently steal information from your network.',
    mitreRef:    'Execution · Impact',
    detections:  45,
    trend:       'down',
    trendPct:    8,
    status:      'monitoring',
    howWeDetect: 'Microsoft Defender for Endpoint scans every file and process in real time on all managed devices. Azure Sentinel cross-correlates endpoint signals with network behaviour to catch malware that evades individual tools.',
    howWeDefend: 'Infected machines are automatically isolated from the network within seconds of detection. Our team performs forensic analysis to confirm no spread occurred, restores from verified backups where needed, and patches the entry point.',
    topThreats:  ['Ransomware attempt on finance workstations (isolated & restored)', 'Malicious PowerShell script on dev server (quarantined)', 'Suspicious macro in HR email attachment (blocked)'],
    businessRisk:'A successful ransomware attack could shut down operations for days. We currently have detection for this threat but recommend adding automated backup integrity checks.',
  },
  {
    id:          'privilege',
    icon:        '🔐',
    name:        'Privilege & Access Escalation',
    businessDesc:'Attackers already inside your network trying to gain administrator-level access so they can do more damage, install backdoors, or access sensitive systems.',
    mitreRef:    'Privilege Escalation · Persistence · Defense Evasion',
    detections:  64,
    trend:       'stable',
    trendPct:    2,
    status:      'protected',
    howWeDetect: 'Sentinel monitors all administrator account activity and flags when a normal account suddenly gains elevated privileges, runs unusual commands, or attempts to disable security software.',
    howWeDefend: 'Privilege escalation attempts are blocked by Azure AD Privileged Identity Management (PIM), which requires time-limited approvals for admin access. Our SOC reviews all elevation events daily.',
    topThreats:  ['Attacker injecting code into trusted process (blocked)', 'Attempt to disable Windows Defender on endpoint (blocked)', 'Service account used outside expected hours (alerted & investigated)'],
    businessRisk:'Admin access means an attacker can install software, create backdoors, or access any data in your organisation.',
  },
  {
    id:          'network',
    icon:        '🌐',
    name:        'Network Reconnaissance & Lateral Movement',
    businessDesc:"Attackers mapping your internal network to find valuable systems and moving between machines — like a burglar walking through every room once inside your building.",
    mitreRef:    'Discovery · Lateral Movement',
    detections:  64,
    trend:       'down',
    trendPct:    5,
    status:      'protected',
    howWeDetect: 'Sentinel network analytics detect unusual internal scanning, remote service connections between machines that do not normally communicate, and access to sensitive file shares outside working hours.',
    howWeDefend: 'Network segmentation limits how far an attacker can move. Our SOC team investigates any lateral movement attempt and isolates affected segments to contain the threat before it reaches critical systems.',
    topThreats:  ['Internal network scanning from compromised workstation (contained)', 'Remote desktop connection between non-related systems (blocked)', 'Access to executive file share from service account (alerted)'],
    businessRisk:'Unrestricted internal movement lets attackers reach your most sensitive systems — finance, HR records, IP databases.',
  },
  {
    id:          'datatheft',
    icon:        '📤',
    name:        'Data Theft & Exfiltration',
    businessDesc:'Attempts to secretly copy and move sensitive business data — customer records, financial data, intellectual property — to locations outside your control.',
    mitreRef:    'Collection · Exfiltration',
    detections:  14,
    trend:       'up',
    trendPct:    40,
    status:      'gap',
    howWeDetect: 'Currently limited. Azure Sentinel detects unusual large file transfers and some known exfiltration patterns, but dedicated Data Loss Prevention (DLP) monitoring is not yet in place across all systems.',
    howWeDefend: 'Network-level egress filtering blocks connections to known malicious destinations. However, without full DLP coverage, sophisticated data theft to legitimate cloud services (e.g., personal OneDrive) may not be detected in time.',
    topThreats:  ['2.3 GB transferred to external IP from database server (detected & blocked)', 'Unusual large email attachment sent externally (alerted)', 'Potential staging of files before exfiltration (monitoring)'],
    businessRisk:'⚠ This is our most significant coverage gap. A data breach could result in regulatory fines (up to 4% of global revenue under GDPR), reputational damage, and loss of customer trust.',
  },
  {
    id:          'remote',
    icon:        '📡',
    name:        'Remote Access & Command & Control',
    businessDesc:"Attackers maintaining hidden, ongoing access to your systems — like a remote control channel that lets them issue commands from anywhere in the world.",
    mitreRef:    'Command & Control',
    detections:  29,
    trend:       'stable',
    trendPct:    3,
    status:      'protected',
    howWeDetect: 'Sentinel DNS analytics and network flow analysis detect communications to known attacker-controlled servers, unusual outbound traffic patterns, and use of protocols designed to hide malicious traffic.',
    howWeDefend: 'Malicious domains and IP addresses are blocked at the firewall level. Our threat intelligence feed is updated daily with new attacker infrastructure. Known C2 frameworks (Cobalt Strike, Metasploit) are specifically hunted.',
    topThreats:  ['Beacon traffic to known C2 infrastructure (blocked)', 'DNS tunnelling attempt (detected & blocked)', 'HTTPS traffic to Tor exit node (blocked)'],
    businessRisk:'Undetected remote access means attackers could persist in your environment for months, quietly stealing data or waiting for the right moment to strike.',
  },
];

export const EXEC_SECURITY_HEALTH = {
  overallRating: 'Good',
  overallScore:  78,
  threatsBlocked:  1247,
  coverageRate:    '75%',
  incidentResolutionRate: '97%',
  avgResponseTime: '4.1 hours',
  criticalGaps:    1,
  statement: 'Your organisation is well-protected against the most common cyber threats. Our monitoring systems detected and blocked over 1,200 threats this period. One significant gap — data theft monitoring — requires investment to achieve full protection.',
};

export const EXEC_NOTABLE_EVENTS = [
  {
    date:     'May 20, 2026',
    title:    'Ransomware Stopped on Finance Systems',
    outcome:  'Contained — No Data Lost',
    color:    '#059669',
    what:     'Attackers attempted to install ransomware (file-encrypting malware) on 2 Finance workstations, likely after gaining access through a phishing email.',
    weDid:    'Our systems automatically isolated the affected machines within seconds. Files were restored from backup. Finance operations continued without interruption.',
    lesson:   'This confirms the value of 24/7 automated monitoring. The attack was stopped before any data was encrypted or business impact occurred.',
  },
  {
    date:     'May 21, 2026',
    title:    'Password Attack Campaign Targeting Staff Accounts',
    outcome:  'Blocked — Accounts Secured',
    color:    '#059669',
    what:     'Criminals ran an automated attack attempting to guess passwords for 142 staff accounts simultaneously, using passwords stolen from other companies.',
    weDid:    'Azure Sentinel detected the pattern in real time and blocked all login attempts. Accounts without multi-factor authentication were temporarily suspended. All staff notified.',
    lesson:   'Attackers are increasingly using credentials leaked from other websites. Enforcing MFA on all accounts is the single most effective defence.',
  },
  {
    date:     'May 22, 2026',
    title:    'Phishing Attack Targeting HR Department',
    outcome:  'Largely Blocked — 3 Emails Opened',
    color:    '#ca8a04',
    what:     'A convincing fake DocuSign email was sent to 14 HR staff members asking them to open an attachment. The email appeared to come from a trusted partner.',
    weDid:    'Microsoft Defender blocked 11 of 14 emails before they could be opened. 3 staff opened the email. No malicious code ran. Staff received immediate security awareness reminders.',
    lesson:   'Phishing remains the #1 entry point for attackers. Quarterly security awareness training and simulated phishing tests reduce click rates by up to 70%.',
  },
  {
    date:     'May 23, 2026',
    title:    'Large Data Transfer to External Address Detected',
    outcome:  'Blocked — Under Investigation',
    color:    '#ea580c',
    what:     '2.3 GB of data was transferred from a database server to an external IP address linked to a Tor anonymity network. This is unusual and potentially malicious.',
    weDid:    'The connection was blocked at the firewall. The database administrator account was immediately suspended and investigated. Evidence collected for forensic review.',
    lesson:   'This highlights our data theft gap. Dedicated Data Loss Prevention tooling would have provided earlier warning before the transfer was attempted.',
  },
];

export const EXEC_NEXT_STEPS = [
  {
    priority: 'Urgent',
    color:    '#dc2626',
    action:   'Close the Data Theft Gap',
    what:     'Deploy Data Loss Prevention (DLP) monitoring across all endpoints and cloud services.',
    why:      'Currently our biggest blind spot — we cannot reliably detect if sensitive data is being copied to personal cloud storage or external devices.',
    when:     'Within 30 days',
    cost:     'Included in existing Microsoft 365 E5 licence — requires configuration only.',
  },
  {
    priority: 'High',
    color:    '#ea580c',
    action:   'Require Multi-Factor Authentication for All Staff',
    what:     'Enforce MFA on every account, especially those without it today (est. 23% of accounts).',
    why:      'MFA blocks 99.9% of automated password attacks. The May 21 attack targeted accounts without MFA first.',
    when:     'Within 2 weeks',
    cost:     'Included in existing Microsoft 365 licence — no additional cost.',
  },
  {
    priority: 'High',
    color:    '#ea580c',
    action:   'Run Security Awareness Training',
    what:     'Complete mandatory phishing-awareness training for all staff (currently 64% complete).',
    why:      'The HR phishing attack in May 22 succeeded in reaching 3 staff. Trained staff are 5x less likely to click malicious links.',
    when:     'Within 3 weeks',
    cost:     'No additional cost — training material available in existing platform.',
  },
  {
    priority: 'Medium',
    color:    '#ca8a04',
    action:   'Test Your Backup & Recovery Plan',
    what:     'Conduct a tabletop exercise simulating a ransomware attack to test recovery procedures.',
    why:      'Our backup systems exist but have not been formally tested in 18 months. The ransomware attack in May 20 was contained — but a successful one would rely entirely on backups.',
    when:     'Within 60 days',
    cost:     'Half-day exercise — no technology cost.',
  },
];

// ─── Incident Summary Data ───────────────────────────────────────────────────

export const INCIDENT_PERIOD = 'May 19 – May 25, 2026';
export const INCIDENT_WEEK   = 'Week 21';

export const INCIDENT_SEVERITY_BREAKDOWN = [
  { severity: 'Critical', count:  9, resolved: 7, open: 2, pct: 3  },
  { severity: 'High',     count: 58, resolved:52, open: 6, pct: 21 },
  { severity: 'Medium',   count:141, resolved:138,open: 3, pct: 50 },
  { severity: 'Low',      count: 72, resolved: 72,open: 0, pct: 26 },
];

export const INCIDENT_CATEGORY_BREAKDOWN = [
  { category: 'Credential Compromise',  count: 48, pct: 17 },
  { category: 'Malware / Ransomware',   count: 35, pct: 13 },
  { category: 'Phishing',               count: 52, pct: 19 },
  { category: 'Suspicious Login',       count: 61, pct: 22 },
  { category: 'Data Exfiltration',      count: 14, pct:  5 },
  { category: 'Lateral Movement',       count: 29, pct: 10 },
  { category: 'Privilege Escalation',   count: 22, pct:  8 },
  { category: 'Other',                  count: 19, pct:  7 },
];

export const TOP_INCIDENTS = [
  {
    id: 'INC-20240589', title: 'Ransomware Execution Attempt – Finance Workstations',
    severity: 'Critical', category: 'Malware / Ransomware', status: 'Resolved',
    detectedAt: '2026-05-20 02:14 UTC', resolvedAt: '2026-05-20 05:47 UTC', mttr: '3h 33m',
    affectedAssets: ['FIN-WS-04', 'FIN-WS-07'], affectedUsers: ['j.smith@acme.com'],
    mitreRef: 'T1486 – Data Encrypted for Impact',
    summary: 'Sentinel KQL alert fired on PowerShell base64-encoded payload execution matching LOLBAS patterns. Endpoint isolation was triggered automatically via Logic App. Forensic review confirmed no lateral spread. Backup restore completed within SLA.',
  },
  {
    id: 'INC-20240601', title: 'Credential Brute-Force – Azure AD Sign-In',
    severity: 'High', category: 'Credential Compromise', status: 'Resolved',
    detectedAt: '2026-05-21 08:55 UTC', resolvedAt: '2026-05-21 11:20 UTC', mttr: '2h 25m',
    affectedAssets: ['Azure AD Tenant'], affectedUsers: ['admin@acme.com', 'it-support@acme.com'],
    mitreRef: 'T1110 – Brute Force',
    summary: 'Password spray campaign targeting 142 accounts from 6 rotated source IPs. Conditional Access blocked sign-ins. Accounts with no MFA were temporarily disabled. Source IPs added to sentinel watchlist.',
  },
  {
    id: 'INC-20240615', title: 'Phishing Campaign – HR Department',
    severity: 'High', category: 'Phishing', status: 'Resolved',
    detectedAt: '2026-05-22 13:30 UTC', resolvedAt: '2026-05-22 16:45 UTC', mttr: '3h 15m',
    affectedAssets: ['M365 Exchange'], affectedUsers: ['hr-team@acme.com (14 mailboxes)'],
    mitreRef: 'T1566.001 – Spearphishing Attachment',
    summary: 'DocuSign-themed phishing email with malicious macro attachment delivered to 14 HR mailboxes. Microsoft Defender ATP quarantined 11 before open; 3 users opened attachment, no macro execution confirmed. Security awareness training triggered.',
  },
  {
    id: 'INC-20240628', title: 'Suspicious Script Execution – Dev Server',
    severity: 'Critical', category: 'Malware / Ransomware', status: 'Investigating',
    detectedAt: '2026-05-24 22:10 UTC', resolvedAt: null, mttr: null,
    affectedAssets: ['DEV-SRV-12'], affectedUsers: ['svc-build@acme.com'],
    mitreRef: 'T1059.001 – PowerShell',
    summary: 'Encoded PowerShell command executed from a compromised service account context. Process tree shows cmd.exe spawning powershell.exe with -enc flag. Investigation ongoing; server isolated pending forensic review.',
  },
  {
    id: 'INC-20240633', title: 'Anomalous Data Transfer to External IP',
    severity: 'High', category: 'Data Exfiltration', status: 'Resolved',
    detectedAt: '2026-05-23 17:02 UTC', resolvedAt: '2026-05-23 20:30 UTC', mttr: '3h 28m',
    affectedAssets: ['DB-PROD-01'], affectedUsers: ['db-admin@acme.com'],
    mitreRef: 'T1048 – Exfiltration Over Alternative Protocol',
    summary: 'Unusual outbound traffic to IP 185.220.x.x (Tor exit node) from database server. 2.3 GB transferred over HTTPS. Connection blocked; IP watchlisted. DBA account credentials reset and reviewed.',
  },
];

export const INCIDENT_RESPONSE_METRICS = {
  mttd:             '1h 48m',
  mttr:             '4h 12m',
  autoContained:    '68%',
  escalated:          14,
  falsePositiveRate:  '8%',
  slaBreached:         3,
  reopened:            2,
};

export const WEEKLY_INCIDENT_TREND = [
  { day: 'Mon May 19', critical: 2, high: 11, medium: 28, low: 14 },
  { day: 'Tue May 20', critical: 3, high: 9,  medium: 24, low: 11 },
  { day: 'Wed May 21', critical: 1, high: 14, medium: 31, low: 15 },
  { day: 'Thu May 22', critical: 2, high: 12, medium: 26, low: 12 },
  { day: 'Fri May 23', critical: 1, high: 8,  medium: 20, low: 10 },
  { day: 'Sat May 24', critical: 0, high: 2,  medium:  8, low:  5 },
  { day: 'Sun May 25', critical: 0, high: 2,  medium:  4, low:  5 },
];

// ─── Compliance Audit Data ───────────────────────────────────────────────────

export const COMPLIANCE_PERIOD = 'Q2 2026 (April 1 – June 30, 2026)';
export const COMPLIANCE_FRAMEWORKS = [
  { id: 'nist-csf',  name: 'NIST CSF 2.0',     version: '2.0',  score: 76, controls: 108, passed: 82, partial: 17, failed: 9  },
  { id: 'iso27001',  name: 'ISO/IEC 27001:2022',version: '2022', score: 81, controls:  93, passed: 75, partial: 12, failed: 6  },
  { id: 'cis-v8',    name: 'CIS Controls v8',   version: 'v8',   score: 71, controls: 153, passed:109, partial: 27, failed:17  },
  { id: 'gdpr',      name: 'GDPR / UK GDPR',    version: '2018', score: 88, controls:  42, passed: 37, partial:  4, failed: 1  },
];

export const NIST_FUNCTIONS = [
  { fn: 'Govern',   score: 79, description: 'Organizational context, risk strategy, roles'     },
  { fn: 'Identify', score: 74, description: 'Asset management, risk assessment'                 },
  { fn: 'Protect',  score: 82, description: 'Access control, awareness training, data security' },
  { fn: 'Detect',   score: 78, description: 'Continuous monitoring, anomalies'                  },
  { fn: 'Respond',  score: 71, description: 'Response planning, communications, mitigation'     },
  { fn: 'Recover',  score: 68, description: 'Recovery planning, improvements, communications'   },
];

export const COMPLIANCE_FINDINGS = [
  {
    id: 'CAF-001', severity: 'Critical', framework: 'CIS v8',
    control: 'CIS 12.6 – Network Infrastructure Management',
    finding: 'Egress filtering rules are inconsistently applied across 3 of 7 network segments. Traffic to known-malicious IP ranges is not blocked on the DMZ segment.',
    risk: 'Enables data exfiltration and C2 communication to bypass perimeter controls.',
    recommendation: 'Implement uniform egress filtering via Azure Firewall Policy with ThreatIntel-based FQDN filtering across all segments.',
    remediation: '2026-06-15', owner: 'Network Security Team',
  },
  {
    id: 'CAF-002', severity: 'High', framework: 'ISO 27001',
    control: 'A.8.3 – Information Access Restriction',
    finding: 'Excessive database read permissions granted to 34 service accounts. Principle of least privilege not enforced for Azure SQL Managed Instance.',
    risk: 'Compromised service account could expose PII and financial records.',
    recommendation: 'Conduct access rights review; implement row-level security and Just-In-Time access via PIM for privileged accounts.',
    remediation: '2026-05-31', owner: 'IAM Team',
  },
  {
    id: 'CAF-003', severity: 'High', framework: 'NIST CSF',
    control: 'PR.DS-1 – Data-at-Rest Protection',
    finding: 'Backup storage accounts for 4 production databases lack customer-managed key (CMK) encryption. Platform-managed keys in use without documented key rotation policy.',
    risk: 'Non-compliance with data protection obligations; potential regulatory exposure.',
    recommendation: 'Migrate to CMK encryption via Azure Key Vault. Define and enforce 90-day key rotation policy.',
    remediation: '2026-06-01', owner: 'Cloud Security Team',
  },
  {
    id: 'CAF-004', severity: 'Medium', framework: 'GDPR',
    control: 'Art. 30 – Records of Processing Activities',
    finding: 'Records of Processing Activities (RoPA) not updated to reflect 3 new data processing workflows introduced in Q1 2026.',
    risk: 'Regulatory non-compliance risk under GDPR Article 83(4) with fines up to €10M.',
    recommendation: 'Update RoPA; appoint data stewards for each workflow; schedule quarterly RoPA reviews.',
    remediation: '2026-05-28', owner: 'Data Protection Office',
  },
  {
    id: 'CAF-005', severity: 'Medium', framework: 'CIS v8',
    control: 'CIS 17.1 – Perform Periodic Security Awareness Training',
    finding: 'Security awareness training completion rate is 64% (target: 95%). 142 employees have overdue training including 12 in privileged roles.',
    risk: 'Elevated phishing susceptibility; human-factor incidents trending upward.',
    recommendation: 'Mandate completion within 14 days; escalate to line managers; automate reminder cadence via HR platform.',
    remediation: '2026-06-08', owner: 'Security Awareness Team',
  },
  {
    id: 'CAF-006', severity: 'Low', framework: 'ISO 27001',
    control: 'A.5.29 – Information Security During Disruption',
    finding: 'Business Continuity Plan (BCP) last tested in November 2024. Annual tabletop exercise overdue by 3 months.',
    risk: 'Untested recovery procedures may fail during an actual incident.',
    recommendation: 'Schedule and conduct BCP tabletop exercise by May 31, 2026. Document results and update plan.',
    remediation: '2026-05-31', owner: 'Business Continuity Manager',
  },
];

export const COMPLIANCE_REMEDIATION_ROADMAP = [
  { quarter: 'Q2 2026 (Current)', items: ['CAF-001 – Egress filtering', 'CAF-002 – Least privilege review', 'CAF-004 – RoPA update', 'CAF-006 – BCP tabletop exercise'] },
  { quarter: 'Q3 2026',           items: ['CAF-003 – CMK encryption rollout', 'CAF-005 – Awareness training to 95%', 'MFA rollout to all remaining accounts', 'DLP policy deployment'] },
  { quarter: 'Q4 2026',           items: ['ISO 27001 external audit preparation', 'CIS Controls v8 IG2 full coverage', 'SOC 2 Type II readiness assessment'] },
];

// ─── Recommendations ─────────────────────────────────────────────────────────

export const RECOMMENDATIONS = [
  {
    priority: 'Critical',
    title:    'Enable DLP monitoring on all endpoints',
    rationale:'No collection or exfiltration controls are in place, leaving sensitive data unprotected.',
    mitreRef: 'TA0009 – Collection, TA0010 – Exfiltration',
    effort:   'Medium',
  },
  {
    priority: 'High',
    title:    'Enforce MFA on all privileged accounts',
    rationale:'Brute-force (T1110) and valid-account abuse (T1078) were the top credential tactics.',
    mitreRef: 'TA0006 – Credential Access',
    effort:   'Low',
  },
  {
    priority: 'High',
    title:    'Deploy EDR with script-block logging',
    rationale:'T1059 (scripting interpreter) ranked #2 by volume with critical severity.',
    mitreRef: 'TA0002 – Execution',
    effort:   'Medium',
  },
  {
    priority: 'Medium',
    title:    'Segment network to limit lateral movement',
    rationale:'T1021 (remote services) detected 11 times; micro-segmentation reduces blast radius.',
    mitreRef: 'TA0008 – Lateral Movement',
    effort:   'High',
  },
  {
    priority: 'Medium',
    title:    'Automate backup integrity verification',
    rationale:'Ransomware (T1486) detected 3 times; verified backups are the primary recovery control.',
    mitreRef: 'TA0040 – Impact',
    effort:   'Low',
  },
];
